import { Box, Center, Divider, HStack, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import type {
  AiSearchIndexKey,
  AiSearchResultsSectionState,
} from '../../types';
import type { AiSearchResultsPageState } from '../../types';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import {
  AI_SEARCH_RESULTS_TAB_LABELS,
  type AiSearchResultsTab,
  INDEX_TO_RESULTS_TAB,
  getAvailableResultsTabs,
} from '../../utils/sectionOrder';
import { PaginatedResultsSection } from './PaginatedResultsSection';

export const ResultsContent: React.FC<{
  state: AiSearchResultsPageState;
  indexData: AiAskIndexData;
}> = ({ state, indexData }) => {
  const title = state.questionPreview?.trim() || 'Search Results';
  const totalMatchCount =
    state.totalCount ??
    state.sections.reduce(
      (matchCount, section) => matchCount + section.hits.length,
      0
    );
  const visibleSections = React.useMemo(
    () => state.sections.filter((section) => section.hits.length > 0),
    [state.sections]
  );
  const hasHits = visibleSections.length > 0;
  const showTabs = visibleSections.length >= 2;

  const availableTabs = React.useMemo(
    () =>
      getAvailableResultsTabs(
        visibleSections.map((section) => section.index as AiSearchIndexKey)
      ),
    [visibleSections]
  );

  const [selectedTab, setSelectedTab] =
    React.useState<AiSearchResultsTab | null>(null);

  const activeTab = React.useMemo(() => {
    if (selectedTab != null && availableTabs.includes(selectedTab)) {
      return selectedTab;
    }
    return availableTabs[0] ?? null;
  }, [availableTabs, selectedTab]);

  const sectionsForTab = React.useMemo(() => {
    if (!showTabs || !activeTab) {
      return visibleSections;
    }

    return visibleSections.filter(
      (section) =>
        INDEX_TO_RESULTS_TAB[section.index as AiSearchIndexKey] === activeTab
    );
  }, [activeTab, showTabs, visibleSections]);

  return (
    <Box maxWidth="container.xl" mx="auto" p={{ base: 3, md: 6 }}>
      <Box bg="bgPrimary" borderRadius="xl" p={{ base: 4, md: 8 }}>
        <HStack justifyContent="space-between" alignItems="flex-end" mb="4">
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="700"
            color="gray.800"
            pr="4"
          >
            {title}
          </Text>
          <Text textStyle="compact" color="gray.600" flexShrink={0}>
            {`Showing total ${totalMatchCount} matches`}
          </Text>
        </HStack>

        {state.idsTruncated ? (
          <Text fontSize="sm" color="gray.600" mb="3">
            Results were truncated to the maximum number of records.
          </Text>
        ) : null}

        <Divider mb="3" />

        {showTabs && activeTab ? (
          <HStack spacing="6" mb="7" color="gray.700" flexWrap="wrap">
            {availableTabs.map((tab) => (
              <Text
                key={tab}
                textStyle="bodyStrong"
                color={activeTab === tab ? 'blue.600' : 'gray.700'}
                textDecoration={activeTab === tab ? 'underline' : 'none'}
                cursor="pointer"
                onClick={() => setSelectedTab(tab)}
              >
                {AI_SEARCH_RESULTS_TAB_LABELS[tab]}
              </Text>
            ))}
          </HStack>
        ) : null}

        <Box maxW="960px" mx="auto">
          {hasHits ? (
            <Stack spacing="0">
              {sectionsForTab.map((section: AiSearchResultsSectionState) => (
                <Box key={section.index}>
                  <PaginatedResultsSection
                    sectionState={section}
                    indexData={indexData}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Center minH={{ base: '40vh', md: '50vh' }} py="10">
              <Text color="gray.600" px="2">
                No records found
              </Text>
            </Center>
          )}
        </Box>
      </Box>
    </Box>
  );
};
